import { memo, useState, useCallback } from 'react';
import { TextField } from '@mui/material';
import { BaseModal } from '@tymely/components';
import { useSetAlert, useCreateOrganizationMutation, useOrganizationsQuery } from '@tymely/services';
import { useForm } from 'react-hook-form';
import { TypeOf, z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { IOrganization } from '@tymely/atoms';

const formSchema = z.object({
    name: z.string().min(1, { message: 'Must not be empty.' }),
    desired_id: z.number().int().optional(),
    client_name: z.string().optional(),
});

type FormInput = TypeOf<typeof formSchema>;

export const NewOrgModal = memo(
    ({
        open,
        onClose,
        onSuccess,
    }: {
        open?: boolean;
        onClose?: () => void;
        onSuccess?: (org: IOrganization) => void;
    }) => {
        const [isSaving, setIsSaving] = useState<boolean>(false);
        const setAlert = useSetAlert();

        const { data: orgs } = useOrganizationsQuery();

        const {
            register,
            formState: { errors, isValid },
            setError,
            handleSubmit,
        } = useForm<FormInput>({
            resolver: zodResolver(formSchema),
        });

        const createOrg = useCreateOrganizationMutation();

        const onSave = useCallback(
            async (values: FormInput) => {
                try {
                    if (orgs?.find((org) => org.name === values.name)) {
                        return setError('name', {
                            type: 'custom',
                            message: 'Organization with this name already exists.',
                        });
                    }
                    setIsSaving(true);
                    const org = await createOrg.mutateAsync(values);
                    setAlert(`Organization "${org.name}" has been created.`, 'success', 3000);
                    onClose?.();
                    setTimeout(() => onSuccess?.(org));
                } finally {
                    setIsSaving(false);
                }
            },
            [orgs],
        );

        return (
            <BaseModal
                open={!!open}
                title="Create new organization"
                okLabel="Save"
                closeLabel="Cancel"
                loading={isSaving}
                disabled={!isValid}
                maxWidth="sm"
                onClose={onClose}
                onOk={handleSubmit(onSave)}
            >
                <form noValidate>
                    <TextField
                        required
                        fullWidth
                        autoFocus
                        autoComplete="off"
                        margin="dense"
                        label="Name"
                        type="text"
                        variant="standard"
                        error={!!errors.name}
                        helperText={errors.name?.message || ''}
                        {...register('name')}
                    />
                    <TextField
                        fullWidth
                        autoComplete="off"
                        margin="dense"
                        label="Desired ID"
                        type="number"
                        variant="standard"
                        error={!!errors.desired_id}
                        helperText={errors.desired_id?.message || ''}
                        {...register('desired_id', {
                            setValueAs: (v) => (v === '' ? undefined : parseInt(v, 10)),
                        })}
                    />
                    <TextField
                        fullWidth
                        autoComplete="off"
                        margin="dense"
                        label="Client name"
                        type="text"
                        variant="standard"
                        error={!!errors.client_name}
                        helperText={errors.client_name?.message || ''}
                        {...register('client_name', {
                            setValueAs: (v) => (v === '' ? undefined : v),
                        })}
                    />
                </form>
            </BaseModal>
        );
    },
);

NewOrgModal.displayName = 'NewOrgModal';
